import { forwardRef, useEffect, useRef, useState } from 'react';
import { jsPDF } from 'jspdf';
import QRCodeLib from 'qrcode';
import SecondaryButton from '../components/SecondaryButton/SecondaryButton';
import { Dialog, DialogContent } from '../components/Dialog';
import StudentList from './StudentList';
import ClassCodeButtons from './ClassCodeButtons';
import SwitchModeButton from './SwitchButton';
import { copyFormatted, getStudentSignUpUrl, noWhiteSpace } from '../GlobalFunctions';
import styles from '../SCSS/ClassCodeManager.module.scss';
import { ClassroomType, UserProfileType } from '@peekapak/types/DataProtocolTypes';

interface Props {
  classroom: ClassroomType;
  userProfile: UserProfileType;
}

const ClassCodeManager = forwardRef((props: Props, ref) => {
  const [showClassCodeEmail, setShowClassCodeEmail] = useState(false);
  const [showCopyConfirmation, setShowCopyConfirmation] = useState(false);
  const [showDownloadConfirmation, setShowDownloadConfirmation] = useState(false);
  const htmlRef = useRef();
  const timeoutIdsRef = useRef<number[]>([]);
  const classCode = props.classroom.classroomId;

  useEffect(() => {
    return () => {
      timeoutIdsRef.current.forEach((id) => {
        window.clearTimeout(id);
      });
      timeoutIdsRef.current = [];
    };
  }, []);

  const scheduleTimeout = (callback: () => void, delay: number) => {
    const timeoutId = window.setTimeout(callback, delay);
    timeoutIdsRef.current.push(timeoutId);
  };

  const onClassCodeEmail = () => setShowClassCodeEmail(true);

  const hideClassCodeEmail = () => setShowClassCodeEmail(false);

  const [registerUrl, registerDisplay] = getStudentSignUpUrl();

  const EmailTextComponent = () => {
    const [pngUrl, setPngUrl] = useState('');
    useEffect(() => {
      let isMounted = true;
      // Generate QR code directly using qrcode library
      const generateQRCode = async () => {
        try {
          const url = await QRCodeLib.toDataURL(`${registerUrl}/${classCode}`, {
            width: 200,
            margin: 1,
          });
          if (isMounted) {
            setPngUrl(url);
          }
        } catch (err) {
          console.error('Error generating QR code:', err);
        }
      };

      void generateQRCode();

      return () => {
        isMounted = false;
      };
    }, [classCode, registerUrl]);
    const text = (
      <div ref={htmlRef} className={styles.emailTextComponent}>
        <div
          style={{
            width: '95%',
            fontFamily: 'GT-Walsheim-Pro-Regular,tahoma,sans-serif',
          }}
        >
          <p className={styles.emailParagraph}>Dear Parent or Guardian:</p>
          <p className={styles.emailParagraph}>
            I&apos;m your child&apos;s teacher, {props.userProfile.salutation} {props.userProfile.lastName}.
          </p>
          <p className={styles.emailParagraph}>
            This year my students will be taking part in Peekapak, a learning program that teaches and reinforces
            social-emotional skills like self-regulation and empathy through story-based learning, hands-on lessons and
            digital learning games within myPeekaville.
          </p>
          <p className={styles.emailParagraph}>
            To add your child to my classroom and to ensure you receive updates on their progress and activities to
            reinforce my lessons, please use this class code,
          </p>
          <h1
            style={{
              textAlign: 'center',
            }}
          >
            <span
              style={{
                color: '#ff6600',
              }}
            >
              {classCode}
            </span>
          </h1>

          <p className={styles.emailParagraph}>when you register your child for an account at the following URL:</p>
          <h4
            style={{
              textAlign: 'center',
            }}
          >
            <strong>
              <a href={`${registerUrl}/${classCode}`} target='_blank' rel='noopener noreferrer'>
                {registerDisplay}
              </a>
            </strong>
          </h4>
          <p className={styles.emailParagraph}>Or scan this QR code to get there without any typing!</p>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
            }}
          >
            <img
              src={pngUrl}
              alt='QR code to sign up'
              style={{
                width: '15%',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            />
          </div>
          <p className={styles.emailParagraph}>
            Your child will also have their own account on myPeekaville, details of which we&apos;ll email to you after
            you register.
          </p>
          <p className={styles.emailParagraph}>Your partner in learning,</p>
          <p className={styles.emailParagraph}>
            {props.userProfile.salutation} {props.userProfile.lastName}
          </p>
          <p className={styles.emailParagraph}>P.S. The best part - Peekapak is no cost to you!</p>
          <p className={styles.emailParagraph}>
            Questions or Feedback? E-mail us at <a href='mailto:hello@peekapak.com'>hello@peekapak.com</a>
          </p>
        </div>
      </div>
    );
    return <>{text}</>;
  };

  const wrapEmailText = (html) => {
    let buffer = noWhiteSpace`
      <html xmlns="http://www.w3.org/1999/xhtml">
         <head>
            <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
            <title>Peekapak Email</title>
            <style type="text/css">
              p { margin: 0.5rem 1rem; }
           </style>
        </head>
        <body>
    `;
    buffer = `${buffer}${html}`;
    buffer = noWhiteSpace`${buffer}
    </body>
    </html>
    `;
    return buffer;
  };

  const wrapPdfText = (html) => {
    const buffer = noWhiteSpace`
      <div class="${styles.emailTextForPrint}">
        ${html}
      </div>
    `;
    return buffer;
  };

  const showWaitMessage = async () => {
    scheduleTimeout(() => {
      setShowDownloadConfirmation(false);
    }, 100);
  };

  const handleDownload = async () => {
    hideClassCodeEmail();
    setShowDownloadConfirmation(true);
    await showWaitMessage();
    saveToPdf(wrapPdfText(htmlRef.current.innerHTML), `LetterToParentsWithClassCode${classCode}.pdf`);
  };

  const ClassCodeIntroduction = () => {
    return (
      <div className={styles.intro}>
        <p>
          This is your Class Code! Please share it with families to set up their child’s accounts and allow parents to
          opt in to your class updates and activities:
        </p>
        <div className={styles.classCode}>{classCode}</div>
        <p>
          Click the button below to email or download a letter to invite parents to set up their child&rsquo;s account.
        </p>
        <div className={styles.buttonsSection}>
          <ClassCodeButtons primary buttonClass={styles.button} {...{ ...props, onClassCodeEmail }} />
          <SwitchModeButton {...props} textButton buttonClass={styles.button} />
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.classCodeManagerContainer}>
        <div className={styles.mainContent}>
          {props.classroom.listOfStudentProxies && props.classroom.listOfStudentProxies.length > 0 ? (
            <StudentList {...{ ...props, onClassCodeEmail }} innerRef={ref} />
          ) : (
            <ClassCodeIntroduction />
          )}
        </div>
      </div>
      <Dialog
        open={showClassCodeEmail}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            hideClassCodeEmail();
          }
        }}
      >
        <DialogContent
          className='w-[min(96vw,780px)] rounded-[0.6rem] border-0 bg-white p-0 text-[1.6rem] shadow-[0_14px_34px_rgba(0,0,0,0.2)]'
          overlayClassName='bg-black/50'
        >
          <div className='relative border-b border-[#dee2e6] px-[2rem] pb-[1.6rem] pt-[1.8rem]'>
            <button
              type='button'
              onClick={hideClassCodeEmail}
              aria-label='Close'
              className='absolute right-[1rem] top-[0.7rem] border-0 bg-transparent p-0 text-[2.4rem] leading-none text-[#242424]'
            >
              x
            </button>
            <h3 className='m-0 text-[2rem] text-[#242424]'>Email/Download Letter to Parent</h3>
          </div>
          <div className='max-h-[60vh] overflow-y-auto px-[1.6rem] py-[1rem]'>
            <EmailTextComponent />
          </div>
          <div className='flex items-center justify-end gap-[1rem] border-t border-[#dee2e6] px-[1.6rem] py-[1.2rem]'>
            <SecondaryButton
              small
              onClick={() => {
                copyFormatted(wrapEmailText(htmlRef.current.innerHTML));
                scheduleTimeout(() => hideClassCodeEmail(), 0);
                scheduleTimeout(() => setShowCopyConfirmation(true), 0);
                scheduleTimeout(() => setShowCopyConfirmation(false), 4100);
              }}
            >
              Copy
            </SecondaryButton>
            <SecondaryButton small onClick={handleDownload}>
              Download
            </SecondaryButton>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showCopyConfirmation}>
        <DialogContent
          className={`${styles.copyConfirmation} w-[min(90vw,420px)] rounded-[0.6rem] border-0 bg-white p-[2rem] text-[1.5rem] shadow-[0_14px_34px_rgba(0,0,0,0.2)]`}
          overlayClassName='bg-black/40'
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <p>Email copied to clipboard!</p>
          <p>You can now paste into your email app.</p>
        </DialogContent>
      </Dialog>
      <Dialog open={showDownloadConfirmation}>
        <DialogContent
          className={`${styles.copyConfirmation} w-[min(90vw,420px)] rounded-[0.6rem] border-0 bg-white p-[2rem] text-[1.5rem] shadow-[0_14px_34px_rgba(0,0,0,0.2)]`}
          overlayClassName='bg-black/40'
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <p>We&apos;re just getting the class code pdf for this class ready...</p>
          <p>Sorry, it may take a little while.</p>
        </DialogContent>
      </Dialog>
    </>
  );
});

function saveToPdf(html: string | HTMLElement, fileName: string | undefined) {
  const doc = new jsPDF({
    orientation: 'p',
    unit: 'pt',
    format: 'a4',
  });
  doc.html(html, {
    callback: (toSave) => {
      toSave.save(fileName);
    },
  });
}
ClassCodeManager.displayName = 'ClassCodeManager';

export default ClassCodeManager;
